// Shared bits for the Processing pages: formatters, badges and the
// loading / error / empty states every page renders.
import type { ReactNode } from 'react';
import {
  AlertCircle,
  Ban,
  BookOpen,
  CheckCircle2,
  Clock,
  FileText,
  Image as ImageIcon,
  Loader2,
  Scissors,
  SkipForward,
  Smartphone,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  JobFeature,
  JobStatus,
  JobTrigger,
  ProcessingResult,
  ResultKind,
  ResultOfKind,
} from '@/types/processing';

// ---- Formatting ----

/** `h:mm:ss` / `m:ss` for a duration or an offset in seconds. */
export function formatOffset(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Inverse of formatOffset: `h:mm:ss`, `m:ss` or plain seconds. Null when unparseable. */
export function parseOffset(text: string): number | null {
  const parts = text.trim().split(':');
  if (parts.length === 0 || parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
}

/** `1h 4m`, `3m 12s`, `42s`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '--';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Wallclock for an epoch-seconds media time. */
export function formatWallclock(epochSeconds: number | null | undefined): string {
  if (epochSeconds == null) return '--';
  return new Date(epochSeconds * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---- Result selection ----

/** Every result of `kind`, oldest first. */
export function resultsOfKind<K extends ResultKind>(
  results: ProcessingResult[] | undefined,
  kind: K,
): ResultOfKind<K>[] {
  return (results ?? [])
    .filter((r): r is ResultOfKind<K> => r.kind === kind)
    .sort((a, b) => a.start - b.start);
}

/** The result of `kind` covering the latest media time, or undefined. */
export function latestOfKind<K extends ResultKind>(
  results: ProcessingResult[] | undefined,
  kind: K,
): ResultOfKind<K> | undefined {
  const all = resultsOfKind(results, kind);
  return all.reduce<ResultOfKind<K> | undefined>(
    (best, r) => (!best || r.end >= best.end ? r : best),
    undefined,
  );
}

// ---- Badges ----

export const FEATURE_META: Record<JobFeature, { label: string; icon: LucideIcon; color: string }> = {
  clip_cut: { label: 'Clip Cut', icon: Scissors, color: 'text-yellow-400' },
  social_format: { label: 'Social Formats', icon: Smartphone, color: 'text-pink-400' },
  thumbnails: { label: 'Thumbnails', icon: ImageIcon, color: 'text-orange-400' },
  final_summary: { label: 'Final Summary', icon: FileText, color: 'text-blue-400' },
};

export const STATUS_META: Record<JobStatus, { label: string; icon: LucideIcon; className: string }> = {
  pending: { label: 'Pending', icon: Clock, className: 'bg-jeket-silver-muted/20 text-jeket-silver-muted' },
  running: { label: 'Running', icon: Loader2, className: 'bg-jeket-gold/20 text-jeket-gold' },
  completed: { label: 'Completed', icon: CheckCircle2, className: 'bg-jeket-healthy/20 text-jeket-healthy' },
  failed: { label: 'Failed', icon: XCircle, className: 'bg-jeket-error/20 text-jeket-error' },
  cancelled: { label: 'Cancelled', icon: Ban, className: 'bg-jeket-silver-muted/20 text-jeket-silver-muted' },
  skipped: { label: 'Skipped', icon: SkipForward, className: 'bg-jeket-info/20 text-jeket-info' },
};

export const TRIGGER_LABEL: Record<JobTrigger, string> = {
  mark: 'Highlight mark',
  manual: 'Manual',
  event_end: 'Event end',
  chain: 'Chained',
};

export function StatusBadge({ status }: { status: JobStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap',
        meta.className,
      )}
    >
      <Icon size={12} className={status === 'running' ? 'animate-spin' : ''} />
      {meta.label}
    </span>
  );
}

export function FeatureBadge({ feature }: { feature: JobFeature }) {
  const meta = FEATURE_META[feature] ?? { label: feature, icon: BookOpen, color: 'text-jeket-silver-muted' };
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
      <Icon size={14} className={meta.color} />
      <span className="text-jeket-silver-bright">{meta.label}</span>
    </span>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="flex-1 h-1.5 bg-jeket-black-deep rounded-full overflow-hidden">
        <div className="h-full bg-jeket-gold rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-jeket-silver-muted tabular-nums">{pct}%</span>
    </div>
  );
}

// ---- Page states ----

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-jeket-silver-bright truncate">{title}</h1>
        {subtitle && <p className="text-sm text-jeket-silver-muted mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-12 gap-2 text-jeket-silver-muted" role="status">
      <Loader2 size={20} className="animate-spin text-jeket-gold" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** Distinct from the empty state — an unreachable backend must not look like "nothing here". */
export function ErrorState({ title, error }: { title: string; error?: unknown }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-jeket-error text-center" role="alert">
      <AlertCircle size={32} className="mb-2 opacity-60" />
      <p className="text-sm">{title}</p>
      {error != null && <p className="text-xs mt-1 text-jeket-error/70 max-w-md break-words">{errorMessage(error)}</p>}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: LucideIcon;
  title: string;
  hint?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-jeket-silver-muted text-center">
      <Icon size={32} className="mb-2 opacity-50" />
      <p className="text-sm">{title}</p>
      {hint && <p className="text-xs mt-1 max-w-md">{hint}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="text-xs text-jeket-error mt-2 break-words" role="alert">
      {errorMessage(error)}
    </p>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn('bg-jeket-black-surface border border-jeket-gold/20 rounded-xl', className)}>
      {children}
    </section>
  );
}

export function SectionTitle({ icon: Icon, children, actions }: { icon: LucideIcon; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2 text-jeket-gold">
        <Icon size={16} />
        <h2 className="text-sm font-medium uppercase tracking-wide">{children}</h2>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export const buttonPrimary =
  'inline-flex items-center gap-2 px-4 py-2 text-sm bg-jeket-gold text-jeket-black-deep font-medium rounded-lg hover:bg-jeket-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const buttonSecondary =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-jeket-gold/10 border border-jeket-gold/30 rounded text-jeket-gold hover:bg-jeket-gold/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const iconButton =
  'p-1.5 rounded hover:bg-jeket-black-elevated text-jeket-silver-muted transition-colors disabled:opacity-50';
export const inputClass =
  'bg-jeket-black-surface border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50';
